import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { recordAbConversions, abExtra } from "@/lib/api";
import { useContactForm } from "@/lib/content";
import { useTurnstile } from "@/components/Turnstile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT =
  "rounded-lg bg-white/10 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-pink-300/60 focus:bg-white/15 transition-colors";

const STD_ORDER = ["name", "email", "phone", "company", "message"];

export default function ContactForm() {
  const cfg = useContactForm();
  const source = useLocation().pathname;
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { Widget: turnstile, token: tsToken, required: tsRequired, reset: tsReset } = useTurnstile();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ph = (k) => `${cfg.fields[k].label}${cfg.fields[k].required ? " *" : ""}`;

  const submit = async (e) => {
    e.preventDefault();
    // Validate enabled required standard fields + required custom fields.
    for (const k of STD_ORDER) {
      const fld = cfg.fields[k];
      if (fld.enabled && fld.required && !(form[k] || "").trim()) {
        return toast.error(`Please fill in "${fld.label}".`);
      }
    }
    for (const c of cfg.custom) {
      if (c.required && !(form[c.key] || "").trim()) {
        return toast.error(`Please fill in "${c.label || c.key}".`);
      }
    }
    if (tsRequired && !tsToken) {
      return toast.error("Please complete the spam check below.");
    }

    const std = {};
    STD_ORDER.forEach((k) => { if (cfg.fields[k].enabled && form[k]) std[k] = form[k]; });
    const extra = { ...abExtra() };
    cfg.custom.forEach((c) => { if (form[c.key]) extra[c.label || c.key] = form[c.key]; });

    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...std, source, extra: Object.keys(extra).length ? extra : undefined, turnstileToken: tsToken || undefined }),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
      recordAbConversions();
      toast.success("Thanks! We'll be in touch within one business hour.");
      setForm({});
      tsReset();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-pink-300" />
        <h3 className="mt-4 font-heading text-xl font-semibold text-white">{cfg.successTitle}</h3>
        <p className="mt-2 text-sm text-slate-300">{cfg.successText}</p>
        <button data-testid="contact-reset-btn" onClick={() => setDone(false)}
          className="mt-6 text-sm font-semibold text-pink-300 hover:text-pink-200 transition-colors">
          Send another message
        </button>
      </div>
    );
  }

  const F = cfg.fields;
  return (
    <form data-testid="contact-form" onSubmit={submit}
      className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 sm:p-7">
      <p className="font-cli text-[11px] uppercase tracking-wider text-pink-300 mb-4">› quick callback</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="contact-name" value={form.name || ""} onChange={update("name")} placeholder={ph("name")} className={INPUT} />
        <input data-testid="contact-email" type="email" value={form.email || ""} onChange={update("email")} placeholder={ph("email")} className={INPUT} />
      </div>
      {F.phone.enabled && (
        <input data-testid="contact-phone" value={form.phone || ""} onChange={update("phone")} placeholder={ph("phone")} className={`mt-3 w-full ${INPUT}`} />
      )}
      {F.company.enabled && (
        <input value={form.company || ""} onChange={update("company")} placeholder={ph("company")} className={`mt-3 w-full ${INPUT}`} />
      )}
      {cfg.custom.map((c) => {
        const val = form[c.key] || "";
        const placeholder = `${c.label || c.key}${c.required ? " *" : ""}`;
        if (c.type === "textarea") {
          return <textarea key={c.key} value={val} onChange={update(c.key)} rows={3} placeholder={placeholder} className={`mt-3 w-full resize-none ${INPUT}`} />;
        }
        if (c.type === "select") {
          return (
            <select key={c.key} value={val} onChange={update(c.key)} className={`mt-3 w-full ${INPUT} ${val ? "" : "text-slate-400"}`}>
              <option value="">{placeholder}</option>
              {(c.options || []).map((o) => <option key={o} value={o} className="text-slate-900">{o}</option>)}
            </select>
          );
        }
        return <input key={c.key} value={val} onChange={update(c.key)} placeholder={placeholder} className={`mt-3 w-full ${INPUT}`} />;
      })}
      {F.message.enabled && (
        <textarea data-testid="contact-message" value={form.message || ""} onChange={update("message")} rows={3} placeholder={ph("message")} className={`mt-3 w-full resize-none ${INPUT}`} />
      )}
      {turnstile}
      <button data-testid="contact-submit-btn" type="submit" disabled={loading}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-pink-400 px-6 py-3.5 text-[15px] font-semibold text-slate-900 hover:bg-pink-300 transition-colors disabled:opacity-60">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Request a callback <Send className="h-4 w-4" /></>}
      </button>
      <p className="mt-3 text-[11px] text-slate-400">{cfg.note}</p>
    </form>
  );
}
